import type { ReactNode } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BackButton from '../BackButton';
import { displayTitleSx } from '../../styles/typography';

// Content aligned with "Hallidays Cookies Policy" (published 15 October 2026)
type Clause = { n: string; lead: string; text: ReactNode };

const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const clauses: Clause[] = [
  {
    n: '1',
    lead: 'What cookies are.',
    text: 'Cookies are small text files that a website places in your web browser. Similar technologies do the same kind of job, including browser storage, which keeps small amounts of information in your browser, and pixels, which report when a page is viewed. In this policy we call all of these cookies.',
  },
  {
    n: '2',
    lead: 'Our own cookies.',
    text: 'Hallidays does not set its own tracking cookies. We use browser storage only to make the website work: if you start our support form, your answers are kept in your browser session so you do not lose them if the page reloads, and they are deleted when you close the browser tab or submit the form.',
  },
  {
    n: '3',
    lead: 'Spam protection.',
    text: (
      <>
        Our Contact Us form uses Google reCAPTCHA to check that enquiries come from a real person and
        not a bot. reCAPTCHA collects information about your device and browser and may set cookies.
        Google's{' '}
        <ExternalLink href="https://policies.google.com/privacy">Privacy Policy</ExternalLink> and{' '}
        <ExternalLink href="https://policies.google.com/terms">Terms of Service</ExternalLink> apply
        to that use.
      </>
    ),
  },
  {
    n: '4',
    lead: 'AI support assistant.',
    text: (
      <>
        When you open the AI Chat on our Support page, the chat service, runs in your browser and may
        store information there so the conversation works. Your messages are processed by the chat
        provider to generate answers. The assistant gives general guidance only, as set out in our{' '}
        <RouterLink to="/terms">Terms of Trade</RouterLink>.
      </>
    ),
  },
  {
    n: '5',
    lead: 'Fonts and page resources.',
    text: 'Our pages load fonts and code. When your browser connects to these services they receive your IP address and basic browser information, as with any website request.',
  },
  {
    n: '6',
    lead: 'Analytics and advertising.',
    text: 'We do not currently use analytics, advertising or social media tracking cookies or pixels on our website. If we start using them, we will update this policy first and describe what they do.',
  },
  {
    n: '7',
    lead: 'Your choices.',
    text: (
      <>
        You can block or delete cookies, and clear browser storage, in your browser settings,
        including blocking third-party cookies only. If you block reCAPTCHA or the chat service, the
        Contact Us form, the support form or the AI Chat may not work, but you can still contact us
        by phone or email. You can learn more about managing cookies at{' '}
        <ExternalLink href="https://www.allaboutcookies.org">www.allaboutcookies.org</ExternalLink>.
      </>
    ),
  },
  {
    n: '8',
    lead: 'Other websites.',
    text: 'Our website links to manufacturer and other third-party websites, for example for owner manuals and service bookings. Those websites have their own cookie and privacy policies, and we are not responsible for how they use cookies.',
  },
  {
    n: '9',
    lead: 'Changes.',
    text: 'We may update this policy when we change the technologies we use. The current version, with the date it was published, is always available at www.hallidays.com.au/cookies-policy.',
  },
  {
    n: '10',
    lead: 'Contact us.',
    text: (
      <>
        For questions about this policy or how we handle your information, contact our Privacy
        Officer at <a href="mailto:admin@hallidays.com.au">admin@hallidays.com.au</a>, on 03 9587
        9900 or 03 5823 1500, or at 30 Brindley Street, Dandenong South VIC 3175.
      </>
    ),
  },
];

const CookiesPolicy = () => {
  return (
    <Box sx={{ py: 6, backgroundColor: '#f8fbff', position: 'relative' }}>
      <BackButton />
      <Container maxWidth="lg">
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h1" align="center" sx={{ ...displayTitleSx, mb: 4 }}>
            Cookie Policy
          </Typography>

          <Typography paragraph sx={{ mb: 3 }}>
            Published 15 October 2026. This Cookie Policy explains how Hallidays (VIC) Pty Ltd (ABN 57
            690 768 060) trading as Hallidays Heating and Cooling (Hallidays, we, us, our) uses cookies
            and similar technologies on www.hallidays.com.au. Read it together with our{' '}
            <RouterLink to="/privacy-policy">Privacy Policy</RouterLink>, which explains how we handle
            personal information.
          </Typography>

          {clauses.map((c) => (
            <Typography paragraph key={c.n} sx={{ mb: 2 }}>
              <strong>{c.n}.</strong> <strong>{c.lead}</strong> {c.text}
            </Typography>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CookiesPolicy;
